import type {EntraIdConfig, EntraIdPreferredDomain} from './EntraIdClient';
import {EntraIdConfigurationError} from './EntraIdErrors';

export type { EntraIdConfig, EntraIdPreferredDomain } from './EntraIdClient';
export interface EntraIdApiConfiguration {
    apiKey: string;
    webApiUrl: string;
}

export type EntraIdValidatedConfiguration = EntraIdConfig & EntraIdApiConfiguration;

export interface EntraIdConfigurationSource {
    getEntraIdClientId(): unknown;
    getEntraIdApiKey(): unknown;
    getEntraIdUrl(): unknown;
    getEntraIdPreferredDomain(): unknown;
    getWebApiUrl(): unknown;
}

/**
 * Validates the runtime settings required by Entra authentication before MSAL is constructed.
 * The returned values are trimmed and URL-normalized; callers must not construct an Entra client
 * from raw environment JSON.
 */
export function readEntraIdConfiguration(source: EntraIdConfigurationSource): EntraIdValidatedConfiguration {
    const clientId = readRequiredString(readValue(source.getEntraIdClientId.bind(source), 'entraIdClientId'), 'entraIdClientId');
    const authUrl = readHttpsUrl(readValue(source.getEntraIdUrl.bind(source), 'entraIdUrl'), 'entraIdUrl');
    const apiKey = readRequiredString(readValue(source.getEntraIdApiKey.bind(source), 'entraIdApiKey'), 'entraIdApiKey');
    const webApiUrl = readHttpsUrl(readValue(source.getWebApiUrl.bind(source), 'webApiUrl'), 'webApiUrl');
    const preferredDomain = readPreferredDomain(
        readValue(source.getEntraIdPreferredDomain.bind(source), 'entraIdPreferredDomain'),
    );
    return {clientId, authUrl, preferredDomain, apiKey, webApiUrl};
}
const readValue = (read: () => unknown, field: string): unknown => {
    try {
        return read();
    } catch {
        throw new EntraIdConfigurationError(field, 'the runtime value is unavailable');
    }
};
const readRequiredString = (value: unknown, field: string): string => {
    if (typeof value !== 'string' || !value.trim()) {
        throw new EntraIdConfigurationError(field, 'a non-empty string is required');
    }
    return value.trim();
};

const readHttpsUrl = (value: unknown, field: string): string => {
    const urlValue = readRequiredString(value, field);
    let url: URL;
    try {
        url = new URL(urlValue);
    } catch {
        throw new EntraIdConfigurationError(field, 'an absolute URL is required');
    }

    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) {
        throw new EntraIdConfigurationError(field, 'an HTTPS URL without credentials, query, or fragment is required');
    }

    return url.toString().replace(/\/$/, '');
};

const readPreferredDomain = (value: unknown): EntraIdPreferredDomain | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (value === 'TEST' || value === 'PROD') {
        return value;
    }
    throw new EntraIdConfigurationError('entraIdPreferredDomain', 'expected TEST, PROD, or no value');
};
